import { type UserDistrictSummary } from "@crowdcoursing/api/openapi";
import { Search as SearchIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import { userDistrictSummaryDisplayName } from "~ui/pages/AdminPage/schoolUserManagementHooks";
import { facultyInitials } from "~ui/pages/VisitorManagement/visitorManagementDisplay";
import { makeProfileAvatarUrl } from "~utils/url";

const tFacultyModalTitle = "Select faculty";
const tFacultyModalSubtitle = "Please choose from the following faculty or staff members.";
const tFacultySearchLabel = "Search";
const tFacultySearchPlaceholder = "Name or email";
const tFacultyTypeToSearch = "Type at least one character to see matches.";
const tNoFaculty = "No faculty or staff found for this school.";
const tNoFacultyMatches = "No matches for your search.";
const tClose = "Close";
const tSignIn = "Sign In";
const tSigningIn = "Signing in…";
const tSignOut = "Sign Out";
const tSigningOut = "Signing out…";
const tBackToList = "Back to list";

export interface VisitorManagementFacultyDialogProps {
  closeFacultyModal: () => void;
  dialogButtonSize: "large" | "medium";
  dialogFullScreen: boolean;
  dialogTitleTypography: "h5" | "h6";
  facultyForSignIn: UserDistrictSummary | null;
  facultyModalOpen: boolean;
  facultySearch: string;
  facultySearchActive: boolean;
  facultyStatus: "error" | "pending" | "success";
  filteredFaculty: UserDistrictSummary[];
  handleFacultySignIn: () => void | Promise<void>;
  handleFacultySignOut: () => void | Promise<void>;
  isKiosk: boolean;
  isSignInPending: boolean;
  isSignOutPending: boolean;
  listAvatarSize: number;
  listItemSx: SxProps<Theme>;
  maxWidth: "md" | "sm";
  setFacultyForSignIn: (user: UserDistrictSummary | null) => void;
  setFacultySearch: (value: string) => void;
  sortedFaculty: UserDistrictSummary[];
}

export function VisitorManagementFacultyDialog({
  closeFacultyModal,
  dialogButtonSize,
  dialogFullScreen,
  dialogTitleTypography,
  facultyForSignIn,
  facultyModalOpen,
  facultySearch,
  facultySearchActive,
  facultyStatus,
  filteredFaculty,
  handleFacultySignIn,
  handleFacultySignOut,
  isKiosk,
  isSignInPending,
  isSignOutPending,
  listAvatarSize,
  listItemSx,
  maxWidth,
  setFacultyForSignIn,
  setFacultySearch,
  sortedFaculty,
}: VisitorManagementFacultyDialogProps) {
  return (
    <Dialog
      fullScreen={dialogFullScreen}
      fullWidth
      maxWidth={maxWidth}
      open={facultyModalOpen}
      onClose={closeFacultyModal}
    >
      {facultyForSignIn ? (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            <Stack alignItems="center" direction="row" spacing={2}>
              <Avatar
                alt=""
                src={facultyForSignIn.userId ? makeProfileAvatarUrl(facultyForSignIn.userId) : undefined}
                sx={{ height: isKiosk ? 72 : 56, width: isKiosk ? 72 : 56 }}
              >
                {facultyInitials(facultyForSignIn)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap fontWeight={600} variant={dialogTitleTypography}>
                  {userDistrictSummaryDisplayName(facultyForSignIn)}
                </Typography>
                {facultyForSignIn.email ? (
                  <Typography color="text.secondary" noWrap variant={isKiosk ? "body1" : "body2"}>
                    {facultyForSignIn.email}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pb: 3 }}>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Button
                color="inherit"
                size={dialogButtonSize}
                sx={{ alignSelf: "flex-start" }}
                variant="text"
                onClick={() => {
                  setFacultyForSignIn(null);
                }}
              >
                {tBackToList}
              </Button>
              <Stack direction={{ sm: "row", xs: "column" }} spacing={2}>
                <Button
                  fullWidth
                  disabled={isSignInPending || !facultyForSignIn.userId}
                  size={dialogButtonSize}
                  variant="contained"
                  onClick={() => {
                    void handleFacultySignIn();
                  }}
                >
                  {isSignInPending ? tSigningIn : tSignIn}
                </Button>
                <Button
                  fullWidth
                  disabled={isSignOutPending || !facultyForSignIn.userId}
                  size={dialogButtonSize}
                  variant="outlined"
                  onClick={() => {
                    void handleFacultySignOut();
                  }}
                >
                  {isSignOutPending ? tSigningOut : tSignOut}
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button size={dialogButtonSize} onClick={closeFacultyModal}>
              {tClose}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>{tFacultyModalTitle}</DialogTitle>
          <DialogContent dividers>
            <Typography color="text.secondary" sx={{ mb: 2 }} variant={isKiosk ? "body1" : "body2"}>
              {tFacultyModalSubtitle}
            </Typography>
            <TextField
              fullWidth
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize={isKiosk ? "medium" : "small"} />
                  </InputAdornment>
                ),
              }}
              label={tFacultySearchLabel}
              placeholder={tFacultySearchPlaceholder}
              size={isKiosk ? "medium" : "small"}
              sx={{ mb: 2 }}
              value={facultySearch}
              onChange={(e) => setFacultySearch(e.target.value)}
            />
            {facultyStatus === "pending" ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress aria-label="Loading faculty" />
              </Box>
            ) : facultyStatus === "error" ? (
              <Typography color="error">Could not load users.</Typography>
            ) : !facultySearchActive ? (
              <Typography color="text.secondary" variant={isKiosk ? "body1" : "body2"}>
                {tFacultyTypeToSearch}
              </Typography>
            ) : sortedFaculty.length === 0 ? (
              <Typography color="text.secondary">{tNoFaculty}</Typography>
            ) : filteredFaculty.length === 0 ? (
              <Typography color="text.secondary">{tNoFacultyMatches}</Typography>
            ) : (
              <List disablePadding>
                {filteredFaculty.map((user) => {
                  const uid = user.userId ?? "";
                  const name = userDistrictSummaryDisplayName(user);
                  return (
                    <ListItem key={uid || name} disablePadding sx={{ display: "block" }}>
                      <ListItemButton
                        alignItems="center"
                        disabled={isSignInPending || isSignOutPending}
                        onClick={() => {
                          setFacultyForSignIn(user);
                        }}
                        sx={listItemSx}
                      >
                        <ListItemAvatar
                          sx={{
                            alignSelf: "center",
                            marginInlineEnd: 0,
                            minWidth: isKiosk ? 72 : 56,
                          }}
                        >
                          <Avatar
                            alt=""
                            src={uid ? makeProfileAvatarUrl(uid) : undefined}
                            sx={{ height: listAvatarSize, width: listAvatarSize }}
                          >
                            {facultyInitials(user)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={name}
                          secondary={user.email ?? undefined}
                          primaryTypographyProps={{ fontWeight: 600, variant: isKiosk ? "body1" : "body2" }}
                          secondaryTypographyProps={{ component: "span", variant: "body2" }}
                          sx={{
                            "& .MuiListItemText-primary": { lineHeight: 1.35 },
                            "& .MuiListItemText-secondary": { mt: 0.25 },
                            margin: 0,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button size={dialogButtonSize} onClick={closeFacultyModal}>
              {tClose}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
